import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useCreateInventoryTransactionType } from '../../../hooks/inventory/useCreateInventoryTransactionType';
import { useUpdateInventoryTransactionType } from '../../../hooks/inventory/useUpdateInventoryTransactionType';

export default function InventoryTransactionTypeForm({ successCallback = null, inventoryTransactionType = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(inventoryTransactionType ? inventoryTransactionType.name : "");
    const [description, setDescription] = React.useState(inventoryTransactionType ? inventoryTransactionType.description : "");

    const { create } = useCreateInventoryTransactionType(successCallback);
    const { update } = useUpdateInventoryTransactionType(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(inventoryTransactionType ? inventoryTransactionType.name : "");
        setDescription(inventoryTransactionType ? inventoryTransactionType.description : "");
    }, [inventoryTransactionType, open]);

    const handleSave = async () => {
        const normalizedName = name.trim().toLowerCase();

        if (!normalizedName) {
            showSnackbar(t('inventory.transaction_types.messages.name_required'), "warning");
            return;
        }

        // ✅ enforce single word only
        if (/\s/.test(normalizedName)) {
            showSnackbar(t('inventory.transaction_types.messages.single_word_required'), "warning");
            return;
        }

        try {
            if (mode === "create") {
                await create({ 
                    name: normalizedName,
                    description: description,
                    custom: true
                });
                showSnackbar(t('inventory.transaction_types.messages.create_success'), "success");
            } else if (mode === "edit" && inventoryTransactionType) {
                await update(inventoryTransactionType.id, { 
                    name: normalizedName,
                    description: description
                });
                showSnackbar(t('inventory.transaction_types.messages.update_success'), "success");
            }
            // clear field after save
            setName("");   
            setDescription(""); 
            // close dialog
            onClose();     
        } catch (err) {
            console.log("err.response", err.response);
            showSnackbar(t('inventory.transaction_types.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setName("");   // clear field on cancel
        setDescription("");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Transaction Type" : "Edit Transaction Type"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="Name (single word only)"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <TextField
                label="Description"
                variant="outlined"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
                <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}